import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import * as API from '../../Utils/API';
import { Person, PersonEvent } from '../../Types/Person';
import { formatMultiParagraphString, maybeHtml } from '../../Utils/HTML';
import parsePhoneNumber from 'libphonenumber-js'
import { Agent } from '../../Types/Agent';
import EventList from './EventList';
import { Copilot } from '../../Types/Copilot';

type Props =
  { person: Person
  , events: PersonEvent[]
  , imageUrl?: string
  , agent?: Agent
  , copilot?: Copilot
  , stripeCustomerId?: string
  , mixpanelProfileUrl?: string
  , formAuthenticityToken: string
  }

const adminImpersonatePath = '/admin/impersonate-specific-user'

const PersonDetails = (props: Props) => {
  const ViewContactCard = () => {
    const [personNotes, setPersonNotes] = useState(props.person.crmNotes);
    const [isEditingNotes, setIsEditingNotes] = useState(false);

    function savePersonNotes() {
      const postBody = {personId: props.person.id, personNotes: personNotes}
      API.post("admin_crm_set_person_notes_path", postBody).then(function (result) {
        if (result['error']) {
        } else {
          setIsEditingNotes(false);
        }
      })
    }

    return (
      <div className="card rounded-2">
        <div className="p-3 border-bottom text-center">
          {maybeHtml(props.imageUrl, (imageUrl) => (
            <div className="avatar avatar-xxl mb-2">
              <img src={imageUrl} className='avatar-img rounded-circle'/>
            </div>
          ))}
          <h3 className="mb-0">{props.person.firstName} {props.person.lastName}</h3>
        </div>
        <div className="p-3 border-bottom text-dark">
          <div className="fs-md fw-semibold mb-2">Basic info</div>
          <div className="">
            <div className="d-flex align-items-center">
              <i className="ai-mail fs-xxl me-2" />
              {props.person.email}
            </div>
            {maybeHtml(props.person.phoneNumber, (phoneNumber) => (
              <div className="d-flex align-items-center mt-1">
                <i className="ai-phone fs-xxl me-2" />
                {parsePhoneNumber(phoneNumber, 'US')?.formatInternational() ?? phoneNumber}
              </div>
            ))}
            <div className="mt-1">Person ID: {props.person.id}</div>
            {maybeHtml(props.person.user, (user) => (
              <div className="mt-1 d-flex align-items-center justify-content-between">
                <div className="">User ID: {user.id}</div>
                <form action={adminImpersonatePath} method="POST" target="_blank">
                  <input type="hidden" name="user_id" value={user.id} />
                  <input type="hidden" name="authenticity_token" value={props.formAuthenticityToken} />
                  <button type="submit" className="btn btn-link p-0">
                    <i className="ai-external-link fs-xl me-1" />
                    Impersonate
                  </button>
                </form>
              </div>
            ))}
            {maybeHtml(props.agent, (agent) => (
              <div className="mt-1">Agent ID: {agent.id}</div>
            ))}
            {maybeHtml(props.copilot, (copilot) => (
              <div className="mt-1 d-flex align-items-center justify-content-between">
                <div className="">Copilot ID: {copilot.id}</div>
                <a href={copilot.profileUrl} target="_blank" className="btn btn-link p-0">
                  <i className="ai-external-link fs-xl me-1" />
                  Profile
                </a>
              </div>
            ))}
            {maybeHtml(props.stripeCustomerId, (stripeCustomerId) => (
              <div className="mt-1 d-flex align-items-center justify-content-between">
                <div className="">Stripe ID: {stripeCustomerId}</div>
                <a href={`https://dashboard.stripe.com/customers/${stripeCustomerId}`} target="_blank" className="btn btn-link p-0">
                  <i className="ai-external-link fs-xl me-1" />
                  Stripe
                </a>
              </div>
            ))}
            {maybeHtml(props.mixpanelProfileUrl, (mixpanelProfileUrl) => (
              <div className="mt-1 d-flex align-items-center justify-content-between">
                <div className="">Mixpanel Profile</div>
                <a href={mixpanelProfileUrl} target="_blank" className="btn btn-link p-0">
                  <i className="ai-external-link fs-xl me-1" />
                </a>
              </div>
            ))}
          </div>
        </div>
        <div className="p-3 text-dark">
          <div className="d-flex align-items-center justify-content-between">
            <div className="fs-md fw-semibold">Notes</div>
            { isEditingNotes
            ? <button onClick={savePersonNotes} className="btn btn-link p-0">Save</button>
            : <button onClick={() => setIsEditingNotes(true)} className="btn btn-link p-0">Edit</button>
            }
          </div>
          { isEditingNotes
          ? <div className="mt-2">
              <textarea
                className="form-control t--person-notes"
                rows={4}
                placeholder={'Enter notes…'}
                onChange={(e) => setPersonNotes(e.target.value) }
                value={personNotes || ''}
              />
            </div>
          : <div className="mt-2">{formatMultiParagraphString(personNotes)}</div>
          }
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="row">
        <div className="col-lg-4">
          <ViewContactCard />
        </div>
        <div className="col-lg-8 mt-3 mt-lg-0">
          <EventList events={props.events} />
        </div>
      </div>
    </div>
  )
}

export default PersonDetails;
